<script setup lang="ts">
import { computed } from "vue";
import { cn } from "@/lib/utils";

interface Props {
    src: string;
    alt?: string;
    size?: "sm" | "lg"; // 'sm' (40px) or 'lg' (64px)
    class?: string;
    hasShadow?: boolean;
    hasBorder?: boolean;
}

const { size = "sm" } = defineProps<Props>();

// Determine size-based values
const dimensions = computed(() => (size === "lg" ? 64 : 40));
</script>

<template>
    <div class="relative" :class="[hasShadow ? 'special-drop-shadow' : '']">
        <svg
            v-if="hasBorder"
            class="absolute top-[-1px] left-[-1px]"
            width="42"
            height="42"
            viewBox="0 0 42 42"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M37.3365 4.66824C35.6755 3.00594 33.568 1.86106 31.2695 1.37231C28.9612 0.84466 25.2121 0.5 21.0069 0.5C16.802 0.5 13.0496 0.844605 10.7445 1.37225C8.44352 1.85857 6.33311 3.00231 4.66944 4.66471C3.00571 6.32717 1.86033 8.4368 1.37228 10.7375C0.844647 13.0458 0.5 16.7948 0.5 21C0.5 25.205 0.844621 28.9575 1.37229 31.2625C1.85983 33.5623 3.00414 35.6712 4.66648 37.3335C6.32887 38.9959 8.43788 40.1403 10.7377 40.6278C13.046 41.1554 16.7949 41.5 21 41.5C25.205 41.5 28.9575 41.1554 31.2626 40.6277C33.5623 40.1402 35.6712 38.9959 37.3335 37.3335C38.9959 35.6711 40.1402 33.5621 40.6278 31.2623C41.1554 28.954 41.5 25.2051 41.5 21C41.5 16.7949 41.1554 13.0424 40.6277 10.7373C40.1406 8.43863 38.9974 6.33044 37.3365 4.66824Z"
                stroke="white"
            />
        </svg>

        <div
            :class="
                cn(
                    'clipped-image shrink-0',
                    `clipped-image-${size}`,
                    $props.class,
                )
            "
            :style="{
                width: `${dimensions}px`,
                height: `${dimensions}px`,
                backgroundImage: `url(${src})`,
            }"
            :aria-label="alt"
            role="img"
        ></div>
    </div>
</template>

<style scoped>
.clipped-image {
    background-size: cover;
    background-position: center;
}

.clipped-image-sm {
    clip-path: path(
        "M30.1618 0.860585C27.9053 0.344234 24.1962 0 20.0069 0C15.8176 0 12.105 0.344234 9.85198 0.860585C7.6441 1.3264 5.61904 2.42344 4.02286 4.0184C2.42668 5.61336 1.32809 7.63758 0.860585 9.84509C0.344234 12.1015 0 15.8107 0 20C0 24.1893 0.344234 27.9019 0.860585 30.1549C1.32759 32.3615 2.42516 34.3851 4.02003 35.98C5.6149 37.5748 7.63848 38.6724 9.84509 39.1394C12.1015 39.6558 15.8107 40 20 40C24.1893 40 27.9019 39.6558 30.1549 39.1394C32.3615 38.6724 34.3851 37.5748 35.98 35.98C37.5748 34.3851 38.6724 32.3615 39.1394 30.1549C39.6558 27.8985 40 24.1893 40 20C40 15.8107 39.6558 12.0981 39.1394 9.84509C38.6729 7.63939 37.5764 5.61644 35.9828 4.02166C34.3892 2.42687 32.3671 1.32878 30.1618 0.860585Z"
    );
}

.clipped-image-lg {
    clip-path: path(
        "M48.2589 1.37694C44.6485 0.550775 38.7139 0 32.011 0C25.3081 0 19.368 0.550775 15.7632 1.37694C12.2306 2.12224 8.99047 3.8775 6.43658 6.42944C3.88269 8.98137 2.12495 12.2201 1.37694 15.7522C0.550775 19.3625 0 25.2971 0 32C0 38.7029 0.550775 44.643 1.37694 48.2478C2.12415 51.7784 3.88025 55.0162 6.43205 57.568C8.98384 60.1198 12.2216 61.8759 15.7522 62.6231C19.3625 63.4492 25.2971 64 32 64C38.7029 64 44.643 63.4492 48.2478 62.6231C51.7784 61.8759 55.0162 60.1198 57.568 57.568C60.1198 55.0162 61.8759 51.7784 62.6231 48.2478C63.4492 44.6375 64 38.7029 64 32C64 25.2971 63.4492 19.357 62.6231 15.7522C61.8767 12.223 60.1222 8.98631 57.5725 6.43465C55.0228 3.883 51.7874 2.12605 48.2589 1.37694Z"
    );
}

.special-drop-shadow {
    filter: drop-shadow(0px 2px 4px rgba(16, 24, 40, 0.06))
        drop-shadow(0px 4px 8px rgba(16, 24, 40, 0.1));
}
</style>
